import { Link, NavLink, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  LayoutDashboard, MessagesSquare, ListTodo, Users, Briefcase, FileText,
  ShieldCheck, Clock, Target, Receipt, CalendarCheck, CalendarDays, Settings, Sun, Moon,
  Menu, LogOut, KeyRound, Search, Mail, FolderOpen, BarChart3,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth, getAccessToken, authenticatedFetch as fetch, captureAuthIdentity } from '../lib/auth-context.js';
import { isStaff } from '@1financial/shared';
import { clsx } from 'clsx';
import { NotificationBell } from './NotificationBell.js';
import { CommandPalette } from './CommandPalette.js';
import { useCapabilities } from '../hooks/useCapabilities.js';

interface ShellProps {
  children: ReactNode;
}

interface NavItem { to: string; label: string; icon: LucideIcon; disabled?: boolean }
const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard',   label: 'Dashboard',   icon: LayoutDashboard },
  { to: '/hub',         label: 'Hub',         icon: MessagesSquare },
  { to: '/my-work',     label: 'My Work',     icon: ListTodo },
  { to: '/meetings',    label: 'Meetings',    icon: CalendarDays },
  { to: '/clients',     label: 'Clients',     icon: Users },
  { to: '/documents',   label: 'Documents',   icon: FolderOpen },
  { to: '/engagements', label: 'Engagements', icon: Briefcase },
  { to: '/proposals',   label: 'Proposals',   icon: FileText },
  { to: '/compliance',  label: 'Compliance',  icon: ShieldCheck },
  { to: '/time',        label: 'Time',        icon: Clock },
  { to: '/goals',       label: 'Goals',       icon: Target },
  { to: '/invoices',    label: 'Invoices',    icon: Receipt },
  { to: '/attendance',  label: 'Attendance',  icon: CalendarCheck },
  { to: '/reports',     label: 'Reports',     icon: BarChart3 },
  { to: '/admin',       label: 'Admin',       icon: Settings },
];

export function Shell({ children }: ShellProps) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { canDo } = useCapabilities();

  const visibleNavItems = user
    ? NAV_ITEMS.filter((item) => {
        if (!isStaff(user)) return false;
        if (item.to === '/invoices') return canDo('billing.view');
        if (item.to === '/proposals') return canDo('proposals.view');
        if (item.to === '/meetings') return canDo('meetings.view');
        if (item.to === '/reports') return canDo('reports.view');
        if (item.to === '/admin') return canDo('admin.access');
        return true;
      })
    : [];

  const sidebar = (
    <aside className="w-60 h-full bg-bg-panel border-r border-border flex flex-col">
      <div className="shrink-0 px-5 py-5 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center font-bold text-accent-fg">1F</div>
          <div>
            <div className="text-sm font-semibold text-white">OPS</div>
            <div className="text-xs text-muted">1Financial</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-1">
        {visibleNavItems.map((item) => {
          const active = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
          return (
            <NavLink
              key={item.to}
              to={item.disabled ? '#' : item.to}
              aria-disabled={item.disabled}
              className={clsx(
                'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
                active && !item.disabled ? 'bg-bg-hover text-white' : 'text-muted hover:text-white hover:bg-bg-hover',
                item.disabled && 'opacity-40 cursor-not-allowed',
              )}
              onClick={(e) => {
                if (item.disabled) e.preventDefault();
                setMobileOpen(false);
              }}
            >
              <item.icon size={16} className="shrink-0" />
              {item.label}
              {item.disabled && (
                <span className="ml-auto text-[10px] uppercase tracking-wider text-muted">soon</span>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="shrink-0 px-3 py-3 border-t border-border">
        <div className="flex items-center gap-2 px-2 py-1.5">
          <div className="w-7 h-7 rounded-full bg-bg-hover flex items-center justify-center text-xs text-white">
            {(user?.displayName ?? user?.email ?? '?').slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-white truncate">{user?.displayName ?? user?.email}</div>
            <div className="text-[11px] text-muted">{user?.role}</div>
          </div>
          <div className="flex items-center gap-1">
            <DigestPrefsButton />
            <ChangePasswordButton />
            <button
              onClick={() => void logout()}
              className="text-muted hover:text-rose-300 p-1.5 rounded-lg hover:bg-bg-hover"
              title="Log out"
              aria-label="Log out"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen flex bg-bg-base">
      {/* Desktop sidebar */}
      <div className="hidden lg:block h-screen sticky top-0">
        {sidebar}
      </div>

      {/* Mobile drawer */}
      <div
        className={clsx(
          'fixed inset-0 z-40 lg:hidden transition-opacity',
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
      >
        <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
        <div
          className={clsx(
            'absolute inset-y-0 left-0 transition-transform duration-200',
            mobileOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          {sidebar}
        </div>
      </div>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0 min-h-screen">
        <TopBar onMenuClick={() => setMobileOpen(true)} />
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">{children}</div>
      </main>

      <CommandPalette />
    </div>
  );
}

function DigestPrefsButton() {
  const [open, setOpen] = useState(false);
  const [optOut, setOptOut] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [testState, setTestState] = useState<'idle' | 'sending' | 'sent' | 'blocked' | 'error'>('idle');
  const { canDo } = useCapabilities();

  useEffect(() => {
    if (!open) return;
    setOptOut(null);
    setTestState('idle');
    fetch('/v1/auth/session', { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((b: { data: { user: { digestOptOut: boolean } } }) => setOptOut(b.data.user.digestOptOut))
      .catch(() => setOptOut(null));
  }, [open]);

  async function toggle(enabled: boolean) {
    setBusy(true);
    try {
      const res = await fetch('/v1/auth/preferences', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify({ digestOptOut: !enabled }),
      });
      if (res.ok) setOptOut(!enabled);
    } finally {
      setBusy(false);
    }
  }

  async function sendTest() {
    setTestState('sending');
    try {
      const res = await fetch('/v1/reports/digest/test', {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) { setTestState('error'); return; }
      const body = await res.json() as { ok: true; data: { sent: number } };
      setTestState(body.data.sent > 0 ? 'sent' : 'blocked');
    } catch {
      setTestState('error');
    }
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-muted hover:text-white p-1.5 rounded-lg hover:bg-bg-hover"
        title="Email preferences"
        aria-label="Email preferences"
      >
        <Mail size={14} />
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-bg-panel border border-border rounded-xl w-full max-w-md mx-4 shadow-2xl p-5 animate-pop">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-white">Email preferences</h2>
              <button onClick={() => setOpen(false)} className="text-muted hover:text-white">✕</button>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={optOut === null ? false : !optOut}
                disabled={optOut === null || busy}
                onChange={(e) => void toggle(e.target.checked)}
                className="mt-0.5 accent-emerald-500"
              />
              <span>
                <span className="block text-sm text-white">Daily morning digest</span>
                <span className="block text-xs text-muted mt-0.5">
                  A summary of your work and deadlines, emailed every day at 10:00 PKT.
                </span>
              </span>
            </label>
            {canDo('users.manage') && (
              <div className="mt-4 pt-4 border-t border-border flex items-center justify-between gap-3">
                <span className="text-xs text-muted">Admin: preview exactly what the 10:00 send looks like for you.</span>
                <button
                  onClick={() => void sendTest()}
                  disabled={testState === 'sending'}
                  className="text-sm btn-ghost-accent border px-3 py-1.5 rounded-lg disabled:opacity-50 whitespace-nowrap"
                >
                  {testState === 'sending' ? 'Sending…' : testState === 'sent' ? 'Sent ✓' : testState === 'blocked' ? 'Not sent — check policy / opt-out' : testState === 'error' ? 'Failed — retry' : 'Send test digest to me'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function ChangePasswordButton() {
  const [open, setOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { logout } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 12) {
      setError('New password must be at least 12 characters');
      return;
    }
    if (newPassword !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/v1/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken() ?? ''}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({} as { error?: { message?: string } }));
        throw new Error(body.error?.message ?? 'Change failed');
      }
      setSuccess(true);
      const isCurrent = captureAuthIdentity();
      setTimeout(() => {
        if (!isCurrent()) return;
        setOpen(false);
        void logout();
      }, 1500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Change failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-muted hover:text-white p-1.5 rounded-lg hover:bg-bg-hover"
        title="Change password"
        aria-label="Change password"
      >
        <KeyRound size={14} />
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-bg-panel border border-border rounded-xl w-full max-w-md mx-4 shadow-2xl p-5 max-h-[90vh] overflow-y-auto animate-pop">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-white">Change password</h2>
              <button onClick={() => setOpen(false)} className="text-muted hover:text-white">✕</button>
            </div>
            {success ? (
              <div className="text-emerald-300 text-sm">Password changed. Signing you out…</div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs text-muted mb-1">Current password</label>
                  <div className="relative">
                    <input
                      type={show ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white pr-14"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShow((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-white text-xs"
                      tabIndex={-1}
                    >
                      {show ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-muted mb-1">New password</label>
                  <input
                    type={show ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white"
                    minLength={12}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted mb-1">Confirm new password</label>
                  <input
                    type={show ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white"
                    required
                  />
                </div>
                {error && <div className="text-rose-400 text-sm">{error}</div>}
                <div className="flex justify-end gap-2 pt-1">
                  <button type="button" onClick={() => setOpen(false)} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
                  <button type="submit" disabled={submitting} className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg">
                    {submitting ? 'Changing…' : 'Change password'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
function TopBar({ onMenuClick }: { onMenuClick: () => void }) {
  const { user } = useAuth();
  return (
    <header className="h-14 bg-bg-panel border-b border-border flex items-center justify-between px-4 sm:px-6 gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <button
          onClick={onMenuClick}
          className="lg:hidden p-1.5 rounded-lg hover:bg-bg-hover text-muted"
          aria-label="Open navigation"
        >
          <Menu size={18} />
        </button>
        <div className="text-sm text-muted truncate">
          <span className="hidden sm:inline">Signed in as </span>
          <span className="text-white">{user?.email}</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => window.dispatchEvent(new Event('open-command-palette'))}
          className="flex items-center gap-1.5 text-xs text-muted hover:text-white border border-border hover:border-accent rounded-lg px-2.5 py-1"
          title="Search (⌘K)"
        >
          <Search size={13} /> <span className="text-[10px]">⌘K</span>
        </button>
        <ThemeToggle />
        <NotificationBell />
      </div>
    </header>
  );
}

function ThemeToggle() {
  const [theme, setTheme] = useState<'dark' | 'light'>(() =>
    document.documentElement.dataset.theme === 'light' ? 'light' : 'dark',
  );
  function toggle() {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    document.documentElement.dataset.theme = next;
    try { localStorage.setItem('ops-theme', next); } catch { /* private mode */ }
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', next === 'light' ? '#f6f8fa' : '#0b0b18');
  }
  return (
    <button
      onClick={toggle}
      className="p-1.5 rounded-lg text-muted hover:text-white hover:bg-bg-hover"
      title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      aria-label="Toggle color theme"
    >
      {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
    </button>
  );
}
